import { Expense } from '../types/Expense';
import { Deposit, DepositStatistics } from '../types/Deposit';
import { Job } from '../types/Job';
import { thisWeek } from './dateRange';

export function aggregateExpenses(
  expenses: Expense[],
): { totalExpenses: number; expenseCount: number } {
  return expenses.reduce(
    (acc, e) => {
      acc.totalExpenses += e.amount;
      acc.expenseCount++;
      return acc;
    },
    { totalExpenses: 0, expenseCount: 0 },
  );
}

export function aggregateDeposits(
  deposits: Deposit[],
  now: Date = new Date(),
): DepositStatistics {
  const { start: startOfWeek } = thisWeek(now);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfYear = new Date(now.getFullYear(), 0, 1);

  return deposits.reduce(
    (acc, d) => {
      acc.totalDeposits++;
      acc.totalAmount += d.amount;
      if (d.status === 'pending') {
        acc.pendingDeposits++;
        acc.pendingAmount += d.amount;
      } else {
        acc.completedDeposits++;
        acc.completedAmount += d.amount;
      }
      if (d.depositDate >= startOfWeek) acc.weeklyTotal += d.amount;
      if (d.depositDate >= startOfMonth) acc.monthlyTotal += d.amount;
      if (d.depositDate >= startOfYear) acc.annualTotal += d.amount;
      return acc;
    },
    {
      totalDeposits: 0,
      totalAmount: 0,
      pendingDeposits: 0,
      completedDeposits: 0,
      pendingAmount: 0,
      completedAmount: 0,
      weeklyTotal: 0,
      monthlyTotal: 0,
      annualTotal: 0,
    },
  );
}

export interface FleetMetrics {
  totalIncome: number;
  totalExpenses: number;
  totalJobs: number;
  cashJobs: number;
  debtJobs: number;
  cashAtHand: number;
  moneyOwed: number;
  incomeChange: number;
  expensesChange: number;
}

export function computeFleetMetrics(
  thisWeekJobs: Job[],
  allJobs: Job[],
  weeklyExpenses: Expense[],
  allExpenses: Expense[],
  allDeposits: Deposit[],
  lastWeekJobs: Job[],
): FleetMetrics {
  const weeklyExpenseTotal = aggregateExpenses(weeklyExpenses).totalExpenses;
  const allExpenseTotal = aggregateExpenses(allExpenses).totalExpenses;
  const totalDeposited = allDeposits
    .filter(d => d.status === 'completed')
    .reduce((sum, d) => sum + d.amount, 0);

  const thisWeekStats = thisWeekJobs.reduce(
    (acc, job) => {
      acc.totalIncome += job.amount;
      acc.totalJobs++;
      if (job.paymentType === 'cash') {
        acc.cashJobs++;
      } else {
        acc.debtJobs++;
        acc.moneyOwed += job.amount;
      }
      return acc;
    },
    { totalIncome: 0, totalJobs: 0, cashJobs: 0, debtJobs: 0, moneyOwed: 0 },
  );

  const allTimeIncome = allJobs.reduce((sum, j) => sum + j.amount, 0);
  const allTimeDebt = allJobs
    .filter(j => j.paymentType === 'debt')
    .reduce((sum, j) => sum + j.amount, 0);
  const cashAtHand = allTimeIncome - allTimeDebt - allExpenseTotal - totalDeposited;

  const lastWeekIncome = lastWeekJobs.reduce((sum, j) => sum + j.amount, 0);
  const incomeChange =
    lastWeekIncome > 0
      ? ((thisWeekStats.totalIncome - lastWeekIncome) / lastWeekIncome) * 100
      : thisWeekStats.totalIncome > 0
        ? 100
        : 0;

  return {
    ...thisWeekStats,
    totalExpenses: weeklyExpenseTotal,
    cashAtHand,
    incomeChange: Math.round(incomeChange * 10) / 10,
    expensesChange: 0,
  };
}
